"use client";

/**
 * Mobile drawer auth section — reads the current Supabase Auth session
 * (browser client) and renders either:
 *   - signed out  → "Sign in" + "Create account" CTAs
 *   - signed in   → "Hi, <name>" + Account / Bookings / Wishlist / Profile links + Sign out
 *
 * Drop-in for the MobileNav drawer. Closes the drawer via the `onClose`
 * callback after every navigation/sign-out (so the user doesn't have to tap
 * the close × manually).
 */

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { createSupabaseBrowserClient } from "@/lib/auth/client";
import { accountIdentityLabel } from "@/lib/auth/phone";
import { signOut } from "@/app/actions/auth";

interface Props {
  onClose: () => void;
}

export default function MobileAuthSection({ onClose }: Props) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "anon" }
    | { kind: "user"; email: string; fullName: string | null }
  >({ kind: "loading" });
  const tc = useTranslations("common");

  useEffect(() => {
    if (
      !process.env.NEXT_PUBLIC_SUPABASE_URL ||
      !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    ) {
      setState({ kind: "anon" });
      return;
    }
    const supabase = createSupabaseBrowserClient();
    let cancelled = false;

    async function load(uid: string, email: string) {
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", uid)
        .maybeSingle<{ full_name: string | null }>();
      if (cancelled) return;
      setState({ kind: "user", email, fullName: data?.full_name ?? null });
    }

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled) return;
      if (user?.email) load(user.id, user.email);
      else setState({ kind: "anon" });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      const u = session?.user;
      if (u?.email) load(u.id, u.email);
      else setState({ kind: "anon" });
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state.kind === "loading") {
    return <div className="h-10" aria-hidden="true" />;
  }

  if (state.kind === "anon") {
    return (
      <div className="px-4 pt-4 pb-3 border-b border-ss-line">
        <Link
          href="/sign-in"
          onClick={onClose}
          className="block w-full text-center bg-ss-text text-white font-semibold py-3 rounded-[12px] hover:opacity-90"
        >
          {tc("signIn")}
        </Link>
        <Link
          href="/sign-up"
          onClick={onClose}
          className="block w-full text-center mt-2 border border-ss-line text-ss-text font-semibold py-3 rounded-[12px] hover:bg-ss-bg-soft"
        >
          {tc("createAccount")}
        </Link>
      </div>
    );
  }

  const initial = (state.fullName?.[0] ?? state.email[0] ?? "?").toUpperCase();
  const identity = accountIdentityLabel(state.email);
  const displayName = state.fullName ?? identity;

  return (
    <div className="px-4 pt-4 pb-3 border-b border-ss-line">
      <div className="flex items-center gap-3 px-1 py-2 mb-1">
        <span className="w-9 h-9 rounded-full bg-ss-blue text-white text-sm font-bold grid place-items-center flex-none">
          {initial}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ss-text truncate">{displayName}</div>
          <div className="text-[12px] text-ss-muted truncate" dir="ltr">{identity}</div>
        </div>
      </div>
      <Link
        href="/account"
        onClick={onClose}
        className="block px-1 py-2 text-[13.5px] text-ss-text hover:text-ss-blue"
      >
        {tc("account")}
      </Link>
      <Link
        href="/account/bookings"
        onClick={onClose}
        className="block px-1 py-2 text-[13.5px] text-ss-text hover:text-ss-blue"
      >
        {tc("trips")}
      </Link>
      <Link
        href="/account/wishlist"
        onClick={onClose}
        className="block px-1 py-2 text-[13.5px] text-ss-text hover:text-ss-blue"
      >
        {tc("wishlist")}
      </Link>
      <Link
        href="/account/profile"
        onClick={onClose}
        className="block px-1 py-2 text-[13.5px] text-ss-text hover:text-ss-blue"
      >
        {tc("profile")}
      </Link>
      <form action={signOut} className="mt-1 pt-1 border-t border-ss-line">
        <button
          type="submit"
          onClick={onClose}
          className="block w-full text-start px-1 py-2 text-[13.5px] text-ss-muted hover:text-ss-text cursor-pointer"
        >
          {tc("signOut")}
        </button>
      </form>
    </div>
  );
}
